import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import withAuth from '../../../components/hoc/withAuth';
import ActionBar from '../../../components/ui/ActionBar';
import NewButton from '../../../components/ui/NewButton';
import OverflowText from '../../../components/ui/OverflowTooltip';
import Loader from '../../../components/ui/Loader';
import DataGrid from '../../../components/DataGrid';
import { toDate, unique } from '../../../utils/utils';
import GridButton from '../../../components/ui/GridButton';
import Icon from '../../../components/ui/Icon';
import {
    getDocumentContentMapping,
    getDocumentsByCompanyId,
    setSubTypes,
    setTypeSubTypeMapping,
} from '../../../ducks/document';
import { AppLoadContext } from '../../../components/ui/AppLoadContext';
import AddDocumentModal from './AddDocumentModal';
import { FORM_ACTION_TYPE } from '../../../constants';
import ViewDocumentModal from './ViewDocumentModal';

const DocumentManagement = () => {
    const dispatch = useDispatch();
    const isAppLoaded = useContext(AppLoadContext);

    const currentUser = useSelector((state) => state.user.currentUser);
    const documentList = useSelector((state) => state.document.documents);
    const documentContentMapping = useSelector(state => state.document.documentContentMap);

    const [addEditModal, setAddEditModal] = useState(false);
    const [viewModal, setViewModal] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [action, setAction] = useState(null);

    const defaultSort = [
        {
            id: 'recordModified',
            desc: true,
        },
    ];

    const columns = [
        {
            id: 'documentType',
            header: 'Document Name',
            accessorKey: 'documentType',
            sortingFn: 'text',
        },
        {
            id: 'contentType',
            header: 'Content Type',
            accessorKey: 'contentType',
            sortingFn: 'text',
        },
        {
            id: 'contentSubType',
            header: 'Sub Type',
            accessorKey: 'contentSubType',
            sortingFn: 'text',
        },
        {
            id: 'description',
            header: 'Description',
            accessorKey: 'description',
            cell: ({ row, cell }) => (<OverflowText id={cell.id}>{row.original.description}</OverflowText>),
            enableColumnFilter: false,
            sortingFn: 'text'
        },
        {
            id: 'agShortName',
            header: 'AG Short Name',
            accessorKey: 'agShortName',
            sortingFn: 'text',
        },
        {
            id: 'agMeetingTitle',
            header: 'AG Meeting Title',
            accessorKey: 'agMeetingTitle',
            cell: ({ row, cell }) => (
                row.original.agMeetingTitle ? <OverflowText id={cell.id}>{row.original.agMeetingTitle}</OverflowText> : ""
            ),
            filterFn: 'equalsString'
        },
        {
            id: 'recordCreated',
            header: 'Created Date',
            accessorKey: 'recordCreated',
            sortType: 'date',
            accessorFn: (row) => (new Date(row.recordCreated)),
            cell: ({ row }) => `${toDate(row.original.recordCreated)}`,
            size: 150,
            enableColumnFilter: false,
            sortingFn: 'datetime'
        },
        {
            id: 'recordModified',
            header: 'Updated Date',
            accessorKey: 'recordModified',
            sortType: 'date',
            accessorFn: (row) => (new Date(row.recordModified)),
            cell: ({ row }) => `${toDate(row.original.recordModified)}`,
            size: 150,
            enableColumnFilter: false,
            sortingFn: 'datetime'
        },
        {
            id: 'actions',
            header: 'Actions',
            size: 90,
            cell: ({ row }) => (
                <>
                    <GridButton variant="light" size="sm" className="me-1 py-0" title="Edit"
                        onClick={() => handleEdit(row.original)}>
                        <Icon icon="edit" size={14} />
                    </GridButton>
                    <GridButton variant="light" size="sm" className="me-1 py-0" title="View"
                                onClick={() => handleView(row.original)}>
                        <Icon icon="view" size={14} />
                    </GridButton>
                </>
            )
        }
    ];

    const handleEdit = (data) => {
        setAddEditModal(true);
        setSelectedDocument(data);
        setAction(FORM_ACTION_TYPE.Edit);
    }

    const handleView = (data) => {
        setViewModal(true);
        setSelectedDocument(data);
    }

    const handleAddModalOpen = () => {
        setAddEditModal(true);
        setAction(FORM_ACTION_TYPE.New);
    };

    const handleAddModalClose = () => {
        setAddEditModal(false);
        setSelectedDocument(null);
        setAction(null);
    }

    const handleViewModalClose = () => {
        setViewModal(false);
        setSelectedDocument(null);
    }

    const createTypeMapping = (types) => {
        let typeMap = {};
        for (let type of types) {
            const subTypeMap = documentContentMapping?.filter(item => item.contentType === type);
            let contentSubTypes = [];
            for (let item of subTypeMap) {
                if (!contentSubTypes.includes(item.contentSubType)) {
                    contentSubTypes.push(item.contentSubType);
                }
                typeMap[type] = [...contentSubTypes];
            }
        }
        return typeMap;
    }

    const getSubTypesFromTypeMap = (typeMap) => {
        let subTypes = [];
        for (let type of Object.keys(typeMap)) {
            subTypes = [...subTypes].concat(typeMap[type]);
        }
        return unique(subTypes);
    }

    useEffect(() => {
        if (isAppLoaded) {
            if (!currentUser) {
                return;
            }
            dispatch(getDocumentsByCompanyId(currentUser?.companyId));
            dispatch(getDocumentContentMapping());
        }
    }, [dispatch, isAppLoaded]);

    useEffect(() => {
        if (documentContentMapping) {
            let contentTypes = [];
            for (let item of documentContentMapping) {
                if (!contentTypes.includes(item.contentType)) {
                    contentTypes.push(item.contentType);
                }
            }

            const typeSubTypeMapping =  createTypeMapping(contentTypes);
            const subTypes = getSubTypesFromTypeMap(typeSubTypeMapping);
            dispatch(setTypeSubTypeMapping(typeSubTypeMapping));
            dispatch(setSubTypes(subTypes));
        }
    }, [documentContentMapping]);

    return (
        <div className="flex-grow-1 mb-4">
            <ActionBar title="Documents">
                <NewButton label="New Document" onClick={handleAddModalOpen} />
            </ActionBar>
            {documentList === null ?
                <Loader />
                :
                <DataGrid
                    columns={columns}
                    data={documentList}
                    sort={defaultSort}
                />
            }

            {addEditModal && (
                <AddDocumentModal
                    show={addEditModal}
                    data={selectedDocument}
                    action={action}
                    onCancel={handleAddModalClose}
                />
            )}

            {viewModal && (
                <ViewDocumentModal
                    document={selectedDocument}
                    show={viewModal}
                    onCancel={handleViewModalClose}
                />
            )}
        </div>
    );
};

export default withAuth(DocumentManagement);
